import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Award, Download, Calendar, Search, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const CMEPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const response = await api.getCMESummary();
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to load CME summary:', err);
      toast.error('Failed to load CME summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.exportCMECredits();
      const blob = new Blob([response.data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CME documentation exported');
    } catch (err) {
      toast.error('Failed to export CME documentation');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="pt-20 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Award className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">CME Credits</h1>
              <p className="text-sm text-slate-500">Track and export your learning activities</p>
            </div>
          </div>

          {/* Credits Summary */}
          <Card className="mb-6 border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Credits Earned</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-slate-900">{summary?.credits_earned || 0}</span>
                    <span className="text-slate-500">hours</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600 mb-1">Total Searches</p>
                  <span className="text-2xl font-semibold text-slate-900">{summary?.total_queries || 0}</span>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">First Activity</span>
                  </div>
                  <p className="text-slate-900 font-medium">
                    {summary?.first_activity 
                      ? new Date(summary.first_activity).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">Last Activity</span>
                  </div>
                  <p className="text-slate-900 font-medium">
                    {summary?.last_activity 
                      ? new Date(summary.last_activity).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-sky-50 rounded-lg border border-sky-200 mb-6">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-sky-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">{summary?.credit_type}</h4>
                    <p className="text-sm text-slate-600">
                      Each evidence search earns 0.25 credits toward self-directed learning.
                      Maximum 20 credits per cycle.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleExport}
                disabled={exporting || !summary?.exportable}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="export-cme"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export Documentation
              </Button>
            </CardContent>
          </Card>

          {/* How It Works */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
              <CardDescription>Understanding CME credit tracking</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Search className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Search for Evidence</h4>
                  <p className="text-sm text-slate-600">Each evidence-based search you perform is tracked.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Award className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Earn Credits</h4>
                  <p className="text-sm text-slate-600">0.25 credits per search (Section 2 self-learning).</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Download className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Export Documentation</h4>
                  <p className="text-sm text-slate-600">Download your activity log for MOC submissions.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <DisclaimerBanner />
    </div>
  );
};

export default CMEPage;
